// Thin client over the backend routes (see docs/endpoints.md).
// Auth: a Bearer session token from GitHub/Google login, stored under
// `session_token` and attached to every request.

const API_BASE = import.meta.env.API_BASE;

// ---- Auth / session token ----
const TOKEN_KEY = "session_token";
export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

/** Send the browser to GitHub to sign in (backend handles the OAuth dance). */
export function loginWithGithub(): void {
  window.location.href = `${API_BASE}/auth/github/start`;
}

/** Send the browser to Microsoft to sign in (backend handles the OAuth dance). */
export function loginWithMicrosoft(): void {
  window.location.href = `${API_BASE}/auth/microsoft/start`;
}

/** After the OAuth redirect the token arrives in the URL fragment (#token=…).
 *  Capture and store it, then clean the URL. Returns true if one was found. */
export function captureTokenFromHash(): boolean {
  const m = location.hash.match(/token=([^&]+)/);
  if (!m) return false;
  setToken(decodeURIComponent(m[1]!));
  history.replaceState(null, "", location.pathname + location.search);
  return true;
}

/** Why a sign-in came back without a session, if it did. The backend puts this
 *  in the fragment rather than answering the callback with JSON, so the person
 *  lands back on the app instead of on a raw error page at the API origin.
 *
 *  Cleans the URL either way, so a reload does not re-show a stale message. */
export function captureAuthOutcome(): "cancelled" | "state" | "failed" | null {
  const m = location.hash.match(/auth=(cancelled|state|failed)/);
  if (!m) return null;
  history.replaceState(null, "", location.pathname + location.search);
  return m[1] as "cancelled" | "state" | "failed";
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

export type Citation = {
  n: number;
  document_id: string;
  title: string;
  heading: string;
};

/** A tool the model invoked while answering (name + the raw JSON args it sent). */
export type ToolCall = { name: string; args: string };

export type AskResponse = {
  answer_id: string;
  answer: string;
  citations: Citation[];
  confidence: number | null; // null when retrieval can't score (tag-fallback / no embedding key)
  scope: unknown;
  tools: ToolCall[];
};

export type SessionResponse = {
  session_id: string;
  target_version: string | null;
  audiences: string[] | null;
};

/** The audience tiers a document can be scoped to / a principal can be granted. */
export const AUDIENCES = ["consumer", "support", "developer"] as const;
export type Audience = (typeof AUDIENCES)[number];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...authHeaders(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    throw new ApiError(0, `Network error: ${(cause as Error).message}`);
  }

  const text = await res.text();

  // The backend always answers JSON, but the platform in front of it does not
  // have to. min_scale is 0 by design, so a first request after idle can be met
  // by the gateway rather than the app - and a gateway error page is HTML.
  // JSON.parse would throw a raw SyntaxError straight past ApiError and surface
  // to the user as "Unexpected token '<'", which says nothing about what failed.
  let data: unknown = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ApiError(
        res.status,
        res.ok
          ? "The server returned a malformed response. Please try again."
          : // A non-JSON error body is the platform talking, not the API; its
            // status is the only trustworthy part of it.
            `Request failed (${res.status}). Please try again.`,
      );
    }
  }
  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && "error" in data
        ? String((data as Record<string, unknown>).error)
        : undefined) ?? res.statusText;
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export type ProductAccess = {
  id: string;
  name: string;
  is_owner: boolean;
  permissions: string[];
  audiences: string[];
};

export type Me = {
  email: string;
  /** Product creation is granted by hand and off by default, so the pages that
   *  offer it have to ask rather than assume. */
  can_create_products: boolean;
  products: ProductAccess[];
};

/** Current member + the products they can access (resolved from the Bearer token). */
export function me(): Promise<Me> {
  return request<Me>("GET", "/me");
}

/** Create a new product; the signed-in member becomes its owner. Returns the
 *  new product shaped like a /me product entry, so the caller can open a session
 *  against it straight away. */
export function createProduct(
  name: string,
  description?: string | null,
): Promise<ProductAccess> {
  return request<ProductAccess>("POST", "/products", {
    name,
    description: description ?? undefined,
  });
}

export function openSession(
  productId: string,
  targetVersion: string | null,
  audiences?: string[],
): Promise<SessionResponse> {
  return request<SessionResponse>("POST", `/products/${productId}/sessions`, {
    target_version: targetVersion ?? undefined,
    // Optional narrowing within the caller's grant; omit to use the full grant.
    audiences: audiences && audiences.length ? audiences : undefined,
  });
}

export function ask(
  sessionId: string,
  question: string,
): Promise<AskResponse> {
  return request<AskResponse>("POST", `/sessions/${sessionId}/ask`, {
    question,
  });
}

/** A regenerated answer produced when an answer is flagged unhelpful. */
export type EscalatedAnswer = {
  answer_id: string;
  answer: string;
  citations: Citation[];
  confidence: number | null;
  supersedes_answer_id: string;
  model: string;
};

export type FlagResponse = {
  flag_id: string;
  status: string;
  escalated: EscalatedAnswer | null;
};

/** Flag an answer as wrong/unhelpful. The server auto-escalates: when possible
 *  it regenerates with a stronger model and returns the new answer. */
export function flagAnswer(answerId: string, reason?: string): Promise<FlagResponse> {
  return request<FlagResponse>("POST", `/answers/${answerId}/flag`, { reason });
}

export type SessionTurn = {
  id: string;
  question: string;
  answer: string;
  citations: Citation[];
  confidence: number | null;
  created_at: string;
  supersedes_answer_id: string | null;
  tools: ToolCall[];
};

export type SessionHistory = {
  session: { id: string; product_id: string; target_version: string | null };
  answers: SessionTurn[];
};

/** Session meta + full turn history, oldest-first, to rehydrate the chat. */
export function getSession(
  sessionId: string,
): Promise<SessionHistory> {
  return request<SessionHistory>("GET", `/sessions/${sessionId}`);
}

export type SessionListItem = {
  id: string;
  created_at: string;
  title: string;
  turns: number;
  target_version: string | null;
};

/** The member's past sessions for a product (most recent first), for resuming.
 *  Pass `limit` for the capped side-menu list; omit it for all sessions. */
export function listSessions(
  productId: string,
  limit?: number,
): Promise<{ sessions: SessionListItem[] }> {
  const q = limit ? `?limit=${limit}` : "";
  return request<{ sessions: SessionListItem[] }>("GET", `/products/${productId}/sessions${q}`);
}

/** Delete a session and its turns (own sessions only). */
export function deleteSession(sessionId: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>("DELETE", `/sessions/${sessionId}`);
}

// ---- Admin (owner) ----

export type ProductSettings = {
  id: string;
  name: string;
  description: string | null;
  versioning_enabled: boolean;
};

export type ProductMember = {
  member_id: string;
  email: string;
  is_owner: boolean;
  permissions: string[];
  audiences: string[];
  accepted_at: string | null;
  invited_at: string;
};

export function getProductSettings(productId: string): Promise<ProductSettings> {
  return request<ProductSettings>("GET", `/products/${productId}`);
}

export function updateProductSettings(
  productId: string,
  patch: { name?: string; description?: string | null; versioning_enabled?: boolean },
): Promise<ProductSettings> {
  return request<ProductSettings>("PATCH", `/products/${productId}`, patch);
}

export function listMembers(productId: string): Promise<{ members: ProductMember[] }> {
  return request<{ members: ProductMember[] }>("GET", `/products/${productId}/members`);
}

export function addMember(
  productId: string,
  body: { email: string; permissions: string[]; audiences: string[] },
): Promise<{ member_id: string; email: string; permissions: string[]; audiences: string[] }> {
  return request("POST", `/products/${productId}/members`, body);
}

export function setMemberPermissions(
  productId: string,
  memberId: string,
  permissions: string[],
  audiences: string[],
): Promise<{ member_id: string; permissions: string[]; audiences: string[] }> {
  return request("PATCH", `/products/${productId}/members/${memberId}`, { permissions, audiences });
}

export function removeMember(
  productId: string,
  memberId: string,
): Promise<{ member_id: string; removed: boolean }> {
  return request("DELETE", `/products/${productId}/members/${memberId}`);
}

export type ApiKeyListItem = {
  id: string;
  name: string;
  permissions: string[];
  audiences: string[];
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
};

export function listApiKeys(productId: string): Promise<{ api_keys: ApiKeyListItem[] }> {
  return request("GET", `/products/${productId}/api-keys`);
}

/** Create a key - the returned `key` is the secret, shown only once. */
export function createApiKey(
  productId: string,
  body: { name: string; permissions: string[]; audiences?: string[]; expires_at?: string | null },
): Promise<{ id: string; name: string; permissions: string[]; audiences: string[]; expires_at: string | null; key: string }> {
  return request("POST", `/products/${productId}/api-keys`, body);
}

export function revokeApiKey(
  productId: string,
  keyId: string,
): Promise<{ id: string; revoked: boolean }> {
  return request("DELETE", `/products/${productId}/api-keys/${keyId}`);
}

// ---- Documents ----

export type DocumentListItem = {
  id: string;
  title: string;
  source: string | null;
  status: string;
  applies_from: string | null;
  applies_to: string | null;
  categories: string[];
  tags: string[];
  audiences: string[];
  created_at: string;
  updated_at: string;
};

export type DocumentFull = DocumentListItem & { body_md: string };

export type NewDocument = {
  title: string;
  body_md: string;
  source?: string;
  categories?: string[];
  tags?: string[];
  audiences: string[]; // required (fail-closed): a doc must declare its visible tiers
  appliesFromVersion?: string;
  appliesToVersion?: string;
  // When true (create only), the server runs an LLM to fill categories/tags. The
  // model never sets audiences. Ignored by PUT/PATCH.
  auto_categorize?: boolean;
};

export async function listDocuments(
  productId: string,
): Promise<DocumentListItem[]> {
  const { documents } = await request<{ documents: DocumentListItem[] }>(
    "GET",
    `/products/${productId}/documents`,
  );
  return documents;
}

export function getDocument(
  productId: string,
  docId: string,
): Promise<DocumentFull> {
  return request<DocumentFull>(
    "GET",
    `/products/${productId}/documents/${docId}`,
  );
}

export function createDocument(
  productId: string,
  doc: NewDocument,
): Promise<{ document_id: string; categories: string[]; tags: string[]; suggested_path: string | null }> {
  return request(
    "POST",
    `/products/${productId}/ingest/docs`,
    doc,
  );
}

/** Full replace (PUT): re-chunks/re-embeds when the body changed. Use when the
 *  body_md was edited. */
export function replaceDocument(
  productId: string,
  docId: string,
  doc: NewDocument,
): Promise<{ document_id: string; body_changed: boolean; re_embedded: boolean }> {
  return request<{ document_id: string; body_changed: boolean; re_embedded: boolean }>(
    "PUT",
    `/products/${productId}/documents/${docId}`,
    doc,
  );
}

/** Metadata-only edit (PATCH): merges the given fields, never touches the body
 *  or embeddings. Use when only title/source/taxonomy/versions changed. */
export type DocumentMeta = Omit<NewDocument, "body_md">;

export function patchDocument(
  productId: string,
  docId: string,
  fields: DocumentMeta,
): Promise<{ document_id: string }> {
  return request<{ document_id: string }>(
    "PATCH",
    `/products/${productId}/documents/${docId}`,
    fields,
  );
}

export function deleteDocument(
  productId: string,
  docId: string,
): Promise<{ document_id: string; deleted: boolean }> {
  return request<{ document_id: string; deleted: boolean }>(
    "DELETE",
    `/products/${productId}/documents/${docId}`,
  );
}

export type GithubImportResult = {
  repo: string;
  ref: string;
  audience: string;
  found: number;
  imported: number;
  updated: number;
  unchanged: number;
  failed: number;
  removed: number;
  reconcile_skipped: "no-files" | "incomplete-listing" | null;
  truncated: boolean;
  failures: { path: string; error: string }[];
};

/** One-shot import of a GitHub repo's .md/.txt files (server fetches + upserts,
 *  applying a single audience; AI fills categories/tags). With `reconcile` (the
 *  default), docs under the repo/subfolder that vanished upstream are deleted. */
export function importFromGithub(
  productId: string,
  body: {
    repo: string;
    audience: string;
    ref?: string;
    path?: string;
    token?: string;
    auto_categorize?: boolean;
    reconcile?: boolean;
  },
): Promise<GithubImportResult> {
  return request<GithubImportResult>("POST", `/products/${productId}/ingest/github`, body);
}
